import { FC, useRef, useEffect } from 'react';
import { IonButton, IonContent, IonModal } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { ISuccessMessage } from '@innerhive-internal/recording-messages';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { formatDuration } from '../../utils/format';

const useStyles = createUseStyles({
    ionContent: {
        '--padding-top': '0',
        '--padding-bottom': '0',
        height: '100%',
        display: 'flex'
    },

    content: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px 32px 24px',
        textAlign: 'center'
    },

    icon: {
        fontSize: 64,
        marginBottom: 24
    },

    title: {
        fontSize: 20,
        fontWeight: 700,
        color: 'var(--ion-color-dark)',
        marginBottom: 8
    },

    message: {
        fontSize: 14,
        marginBottom: 32,
        lineHeight: 1.5,
        maxWidth: 280
    },

    statsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginBottom: 32,
        padding: '16px 24px',
        backgroundColor: 'var(--ion-color-light)',
        borderRadius: 8,
        width: '100%',
        maxWidth: 280
    },

    stat: {
        fontSize: 13,
        color: 'var(--ion-color-medium-shade)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },

    statValue: {
        fontWeight: 600,
        color: 'var(--ion-color-dark)'
    },

    button: {
        '--padding-start': '32px',
        '--padding-end': '32px',
        '--padding-top': '12px',
        '--padding-bottom': '12px',
        fontSize: 16,
        '--border-radius': '8px',
        width: '100%',
        maxWidth: 280
    },

    secondaryButton: {
        width: '100%',
        maxWidth: 280,
        marginBottom: 12
    }
});

interface IProcessingCompleteModalProps {
    isOpen: boolean;
    message: ISuccessMessage | null;
    trackingType: 'milestone' | 'encouragement';
    trackingTitle: string;
    trackingMessage: string;
    trackingRecordingCount: number;
    trackingMilestone: number | null;
    trackingTimePresent: number | null;
    trackingHandwritingSaved: number | null;
    trackingDuration: number | null;
    onViewSummary?: () => void;
    onDismiss: () => void;
    onDidDismiss: () => void;
}

export const ProcessingCompleteModal: FC<IProcessingCompleteModalProps> = ({
    isOpen,
    message,
    trackingType,
    trackingTitle,
    trackingMessage,
    trackingRecordingCount,
    trackingMilestone,
    trackingTimePresent,
    trackingHandwritingSaved,
    trackingDuration,
    onViewSummary,
    onDismiss,
    onDidDismiss
}) => {
    const classes = useStyles();
    const mixpanel = useInstance(MixPanelEventEmitter);
    const hasTrackedRef = useRef(false);

    useEffect(() => {
        if (isOpen) {
            hasTrackedRef.current = false;
        }
    }, [isOpen]);

    const trackAcknowledgment = (response: 'View Summary' | 'Dismissed') => {
        if (hasTrackedRef.current) return;
        hasTrackedRef.current = true;

        mixpanel.track('Processing Success Acknowledged', () => ({
            'Response': response,
            'type': trackingType,
            'title': trackingTitle,
            'message': trackingMessage,
            'recordingCount': trackingRecordingCount,
            'milestone': trackingMilestone,
            'timePresent': trackingTimePresent,
            'handwritingSaved': trackingHandwritingSaved,
            'durationSeconds': trackingDuration ? Math.round(trackingDuration / 1000) : null
        }));
    };

    const handleDidDismiss = () => {
        trackAcknowledgment('Dismissed');
        onDidDismiss();
    };

    if (!message) {
        return null;
    }

    const getIcon = () => {
        switch (message.type) {
            case 'milestone':
                return '🎉';
            case 'encouragement':
                return '👏';
            default:
                return '✓';
        }
    };

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={handleDidDismiss}
            backdropDismiss={true}
            data-id="processing-complete-modal"
        >
            <IonContent className={classes.ionContent}>
                <div className={classes.content}>
                    <div className={classes.icon}>
                        {getIcon()}
                    </div>

                    <div className={classes.title} data-type="modal-title">
                        {message.title}
                    </div>

                    <div className={classes.message}>
                        {message.message}
                    </div>

                    {message.stats && (message.stats.timePresent >= 30 || message.stats.handwritingSaved >= 3) && (
                        <div className={classes.statsContainer}>
                            {message.stats.timePresent >= 30 && (
                                <div className={classes.stat}>
                                    <span>🎯 Time present</span>
                                    <span className={classes.statValue}>{formatDuration(message.stats.timePresent)}</span>
                                </div>
                            )}
                            {message.stats.handwritingSaved >= 3 && (
                                <div className={classes.stat}>
                                    <span>✍️ Handwriting saved</span>
                                    <span className={classes.statValue}>{message.stats.handwritingSaved} pages</span>
                                </div>
                            )}
                        </div>
                    )}

                    {onViewSummary && (
                        <IonButton
                            fill="clear"
                            color="primary"
                            onClick={() => {
                                trackAcknowledgment('Dismissed');
                                onDismiss();
                            }}
                            data-id="dismiss-button"
                            className={classes.secondaryButton}
                        >
                            Maybe Later
                        </IonButton>
                    )}

                    {onViewSummary ? (
                        <IonButton
                            expand="block"
                            className={classes.button}
                            onClick={() => {
                                trackAcknowledgment('View Summary');
                                onViewSummary();
                                onDismiss();
                            }}
                            data-id="view-summary-button"
                        >
                            View Summary
                        </IonButton>
                    ) : (
                        <IonButton
                            expand="block"
                            className={classes.button}
                            onClick={() => {
                                trackAcknowledgment('Dismissed');
                                onDismiss();
                            }}
                            data-id="got-it-button"
                        >
                            Got It!
                        </IonButton>
                    )}
                </div>
            </IonContent>
        </IonModal>
    );
};
